/**
 * Hook for resolving human-readable labels from CRM schema data.
 *
 * Aggregates data from stages (via useStages), property types, and object types
 * to provide label resolution functions for charts and data tables.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { fetchWithDefaults } from '@/lib/http/client';

import { getTypeLabelByName } from '../utils/getTypeLabel';
import {
  formatFallback,
  getOptionLabel as getOptionLabelUtil,
} from '../utils/labelFormatters';
import { useEntityTypeLabel } from './useEntityTypeLabel';
import type { PropertyTypeSchema } from './usePropertySchema';
import { useStages } from './useStages';

/**
 * Fetch all property types (without object type filter).
 */
async function fetchAllPropertyTypes(
  orgId: number
): Promise<PropertyTypeSchema[]> {
  const url = `/api/orgs/${orgId}/crm-events/schemas/property-types/`;

  const response = await fetchWithDefaults(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch property types: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Parse a Cube.js dimension name to extract property context.
 *
 * @example
 * parseDimensionName('CrmObject.ahoy_lifecycle_stage')
 * // { cube: 'CrmObject', property: 'ahoy_lifecycle_stage' }
 */
function parseDimensionName(
  dimensionName: string
): { cube: string; property: string } | null {
  if (!dimensionName) {
    return null;
  }

  const parts = dimensionName.split('.');
  if (parts.length !== 2) {
    return null;
  }

  return { cube: parts[0], property: parts[1] };
}

/**
 * Property names to try when looking up select options for a dimension.
 *
 * Report dimensions are named after their Cube field: builtin properties
 * lose their `ahoy_` prefix (`companies.company_type`) and custom properties
 * gain a `custom_` prefix (`companies.custom_org_tier`). The option list is
 * keyed by the real `property_type_name`, so try the raw name first, then
 * both normalised forms.
 */
function optionPropertyCandidates(property: string): string[] {
  const candidates = [property];
  if (property.startsWith('custom_')) {
    candidates.push(property.slice('custom_'.length));
  } else if (!property.startsWith('ahoy_')) {
    candidates.push(`ahoy_${property}`);
  }
  return candidates;
}

export interface UseSchemaLabelsResult {
  /**
   * Get label for a pipeline stage by its entity_id.
   * Falls back to formatted value if not found.
   */
  getStageLabel: (stageEntityId: string) => string;

  /**
   * Get label for a property option.
   * Falls back to raw value if not found.
   */
  getOptionLabel: (propertyName: string, value: string) => string;

  /**
   * Get label for a CRM entity type.
   * Falls back to formatted value if not found.
   */
  getEntityTypeLabel: (typeName: string) => string;

  /**
   * Resolve a label with smart inference from dimension context.
   * Tries all lookup methods and falls back to formatted value.
   */
  resolveLabel: (value: string, dimensionName?: string) => string;

  /**
   * Combined loading state from all schema queries.
   */
  isLoading: boolean;

  /**
   * Combined error from any schema query.
   */
  error: Error | null;
}

/**
 * Hook to fetch and aggregate CRM schema data for label resolution.
 *
 * @param orgId - Organization ID
 * @returns Label resolution functions and loading state
 *
 * @example
 * const { getStageLabel, resolveLabel } = useSchemaLabels(orgId);
 * const label = getStageLabel('stage-entity-id'); // 'Qualification'
 * const inferred = resolveLabel('stage-entity-id', 'CrmObject.ahoy_lifecycle_stage'); // 'Qualification'
 */
export function useSchemaLabels(
  orgId: number | undefined
): UseSchemaLabelsResult {
  // Fetch stages via useStages hook (CRM objects)
  const {
    getStageByEntityId,
    isLoading: stagesLoading,
    error: stagesError,
  } = useStages(orgId);

  // Fetch all property types
  const {
    data: propertyTypes = [],
    isLoading: propsLoading,
    error: propsError,
  } = useQuery({
    queryKey: ['property-types-all', orgId],
    queryFn: () => fetchAllPropertyTypes(orgId ?? 0),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minute cache
  });

  // Fetch all entity types. Delegated to useEntityTypeLabel so the
  // object-types query and the label callback have a single definition —
  // this hook is the heavyweight superset, not a second implementation.
  const {
    entityTypes,
    getEntityTypeLabel,
    isLoading: entityTypesLoading,
    error: entityTypesError,
  } = useEntityTypeLabel(orgId);

  // Memoized stage label getter - now uses entity_id
  const getStageLabel = useCallback(
    (stageEntityId: string): string => {
      const stage = getStageByEntityId(stageEntityId);
      return stage?.label ?? formatFallback(stageEntityId);
    },
    [getStageByEntityId]
  );

  // Memoized option label getter
  const getOptionLabel = useCallback(
    (propertyName: string, value: string): string => {
      const label = getOptionLabelUtil(propertyName, value, propertyTypes);
      return label ?? value; // Return raw value as fallback for options
    },
    [propertyTypes]
  );

  // Smart label resolver with context inference
  const resolveLabel = useCallback(
    (value: string, dimensionName?: string): string => {
      if (!value) {
        return '';
      }

      const parsed = parseDimensionName(dimensionName || '');

      // If dimension hints at stage property, try stage lookup first
      // Value is now a entity_id for stages
      if (
        parsed?.property?.includes('stage') ||
        parsed?.property?.includes('lifecycle') ||
        parsed?.property?.includes('pipeline')
      ) {
        const stage = getStageByEntityId(value);
        if (stage) {
          return stage.label;
        }
      }

      // If dimension hints at entity type, try entity type lookup via the
      // canonical helper. getTypeLabelByName already rejects *_timeline
      // identifiers (those are event-stream types, not entity types) and
      // falls back through the schema → built-in registry chain.
      //
      // Only unconditionally short-circuit for the two canonical entity-type
      // dimensions (entity_type_name / object_type). For other *_type
      // dimensions (mime_type, location_type, source_type, etc.) the value
      // is typically an option key — try entity lookup first, but fall
      // through to option lookup below if it doesn't match a real entity.
      if (
        parsed?.property === 'entity_type_name' ||
        parsed?.property === 'object_type'
      ) {
        return getTypeLabelByName(value, {}, entityTypes);
      }
      if (parsed?.property?.endsWith('_type')) {
        const entitySchema = entityTypes.find(
          o => o.entity_type_name === value
        );
        if (entitySchema) {
          return entitySchema.label;
        }
      }

      // If dimension hints at a specific property, try option lookup
      if (parsed?.property && !parsed.property.includes('stage')) {
        for (const candidate of optionPropertyCandidates(parsed.property)) {
          const optLabel = getOptionLabelUtil(candidate, value, propertyTypes);
          if (optLabel) {
            return optLabel;
          }
        }
      }

      // Try stage lookup (value might be a entity_id)
      const stage = getStageByEntityId(value);
      if (stage) {
        return stage.label;
      }

      // Try entity type lookup via the canonical helper. getTypeLabelByName
      // rejects *_timeline (returns "Unknown Entity") and otherwise falls
      // through schema → built-ins → snake-case formatting, so an explicit
      // formatFallback isn't needed afterwards.
      return getTypeLabelByName(value, {}, entityTypes);
    },
    [getStageByEntityId, propertyTypes, entityTypes]
  );

  // Aggregate loading state
  const isLoading = stagesLoading || propsLoading || entityTypesLoading;

  // Get first error if any
  const error = useMemo(() => {
    return stagesError || propsError || entityTypesError || null;
  }, [stagesError, propsError, entityTypesError]);

  return {
    getStageLabel,
    getOptionLabel,
    getEntityTypeLabel,
    resolveLabel,
    isLoading,
    error,
  };
}
